// 3rd Party
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

// Asset Components
import arrowback from "../../assets/arrow_back.svg";

// Config
import { appConfig } from "../../config/appConfig";

// Components
import Spinner from "../../components/spinner/Spinner";

// Services
import {
  fetchSasUrl,
  logout,
  uploadToAzureBlob,
} from "../../services/IndiaService";
import { getStorageItemLocal } from "../../services/storageServices";

// Styles (Own style)
import "./IndiaPdf.scss";

type AdminEntry = {
  [x: string]: any;
  id: number;
  pdfName: string;
  pdfUrls: string | string[];
  isActive: boolean;
  status: string;
};

interface IndiaPDFFormDialogProps {
  open: boolean;
  onClose?: () => void;

  fetchData: () => void;
  resetForm: () => void;

  formData: AdminEntry;
  setFormData: React.Dispatch<React.SetStateAction<AdminEntry>>;
  editingIndex: any;
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingIndex: React.Dispatch<React.SetStateAction<any>>;
  dataList: any;
}

const IndiaPDFFormDialog: React.FC<IndiaPDFFormDialogProps> = ({
  open,
  onClose,
  fetchData,
  resetForm,
  formData,
  setFormData,
  editingIndex,
  setShowForm,
  setEditingIndex,
  dataList,
}) => {
  // useState
  const [selectedPdf, setSelectedPdf] = useState<File[]>([]);
  const [isActive, setIsActive] = useState(false);
  const [fileName, setfileName] = useState("No PDF");
  const [loading, setLoading] = useState(false);
  const [pdferror, setPdfError] = useState<string | null>(null);

  // useNavigate
  const navigate = useNavigate();

  // Constants
  const MAX_FILE_SIZE_MB1 = 15;
  const ALLOWED_TYPE = "application/pdf";

  useEffect(() => {
    setIsActive(formData.isActive);

    let temp: string[] = [];

    if (Array.isArray(formData.pdfUrls)) {
      temp = formData.pdfUrls.map((img) => img?.trim?.()).filter(Boolean);
    } else if (typeof formData.pdfUrls === "string") {
      temp = formData.pdfUrls
        .split(",")
        .map((img) => img.trim())
        .filter(Boolean);
    }

    if (temp.length > 0) {
      const extractedFileName = temp[0].split("/").pop()?.trim() ?? "";

      setfileName(extractedFileName);
    } else {
      setfileName(""); // clear filename fallback
    }
  }, [formData]);

  const handleCancel = () => {
    onClose?.();
    setShowForm(false);
    setEditingIndex(null);
    resetForm();
    setShowForm(false);
    setPdfError(null);
    setIsActive(false);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      //   [e.target.name]: e.target.value,
      pdfName: e.target.value,
      lastUpdated: new Date().toISOString(),
    });
  };
  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "application/pdf"
  ) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      if (type === "application/pdf") {
        setSelectedPdf(filesArray);
      }
    }
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];

    if (files.length === 0) {
      setPdfError("Please select a PDF file.");
      return;
    }

    const file = files[0]; // Assuming single file upload for PDF

    if (file.type !== ALLOWED_TYPE) {
      setPdfError("Only .pdf files are allowed.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB1 * 1024 * 1024) {
      setPdfError("File size must be less than or equal to 10MB.");
      return;
    }

    setPdfError(null);
    handleFileChange(e, "application/pdf"); //file.name
    setfileName(file.name);
  };

  const uploadToBlob = async (file: File) => {
    let TokenUrl = await fetchSasUrl("file-uploads");
    let pathPrefix = "IndiaPDFs";
    let fileUrl = await uploadToAzureBlob(
      file,
      "file-uploads",
      TokenUrl,
      pathPrefix
    );

    // alert(fileUrl);
    return fileUrl.split("?")[0];
    // return fileUrl.split("?")[0] + "/" + file.name;
  };
  const handleSave = async () => {
    if (!formData.pdfName) {
      alert("PDF Name is required!");
      return;
    }
    if (dataList.length >= 3 && editingIndex === null) {
      alert("You cannot add more than three PDFs in India PDF.");
      return;
    }
    const formDataToSend = new FormData();
    formDataToSend.append("id", editingIndex?.toString() ?? "0");
    formDataToSend.append("pdfName", formData.pdfName);
    formDataToSend.append("isActive", isActive.toString());
    formDataToSend.append("status", "Pending"); //Pending for Approval

    const username = getStorageItemLocal("username") ?? "";
    formDataToSend.append("uploadBy", username);

    setLoading(true);
    try {
      if (selectedPdf.length > 0) {
        const uploadPromises = selectedPdf.map(async (pdf) => {
          const fileUrl = await uploadToBlob(pdf);
          if (fileUrl) formDataToSend.append("pdfUrls", fileUrl);
        });
        await Promise.all(uploadPromises);
      } else if (Array.isArray(formData.pdfUrls)) {
        for (const url of formData.pdfUrls) {
          const cleanUrl = typeof url === "string" ? url.trim() : "";
          if (cleanUrl && cleanUrl !== "null") {
            formDataToSend.append("pdfUrls", cleanUrl);
          }
        }
      } else if (typeof formData.pdfUrls === "string" && formData.pdfUrls.trim()) {
        const cleanUrl = formData.pdfUrls.trim();
        if (cleanUrl !== "null") {
          formDataToSend.append("pdfUrls", cleanUrl);
        }
      }

      const url =
        editingIndex !== null
          ? `${appConfig.serviceUrls.saveIndiaPDFEntries}/${formData.id}`
          : `${appConfig.serviceUrls.saveIndiaPDFEntries}/upload`;

      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(url, {
        method: editingIndex !== null ? "PUT" : "POST",
        body: formDataToSend,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        logout(navigate);
        return;
      }
      if (!response.ok) throw new Error("Failed to save data");
      alert("Data saved successfully");
      fetchData();
      resetForm();
      const pdfInput = document.getElementById("pdfUpload") as HTMLInputElement | null;
      if (pdfInput) pdfInput.value = "";
      setSelectedPdf([]);
      setEditingIndex(null);
      setShowForm(false);
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Failed to save data");
    } finally {
      setLoading(false);
    }
    handleCancel();
  };
  const handleIsActive = () => {
    setIsActive(!isActive);
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "900px",
          height: "350px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#F7F7F7",
          padding: "20px 16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <IconButton
            color="primary"
            size="small"
            onClick={handleCancel}
            className="custom-icon-button"
          >
            <img src={arrowback} alt="visible" className="custom-icon" />
          </IconButton>
          <Typography className="custom-icon-text">
            {editingIndex !== null
              ? "Edit India Level PDF"
              : "Add India Level PDF"}
          </Typography>
        </Box>

        <Box></Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          background: "#FFF",
          padding: "50px 16px",
        }}
      >
        {loading ? (
          <>
            {" "}
            <Spinner></Spinner>
          </>
        ) : (
          <> </>
        )}
        <Box>
          <Typography className="text-style">PDF Name</Typography>
          <Box className="pdf-name-wrapper1">
            <TextField
              placeholder="Enter PDF Name"
              size="small"
              name="pdfName"
              value={formData.pdfName}
              onChange={handleChange}
              variant="standard"
              fullWidth
              InputProps={{
                disableUnderline: true,
              }}
              inputProps={{
                maxLength: 24,
              }}
            />
            <Typography className="pdf-char-count">
              {formData.pdfName?.length || 0}/24
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography className="text-style">Add PDF File</Typography>
          <Box className="pdf-upload-row">
            <Box className="pdf-name-wrapper">
              <TextField
                placeholder="Select PDF"
                size="small"
                name="pdfName"
                value={fileName}
                onChange={handleChange}
                variant="standard"
                fullWidth
                disabled={true}
                InputProps={{ disableUnderline: true }}
              />
            </Box>

            <Box>
              <Button component="label" className="choose-pdf-button">
                <Typography className="pdf-button-text ">Browse</Typography>
                <input
                  id="pdfUpload"
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={handlePdfUpload}
                />
              </Button>
              {pdferror && <Alert severity="error">{pdferror}</Alert>}
            </Box>
          </Box>
        </Box>
        <Box>
          <Typography className="text-style">Active/Disable</Typography>

          <Box
            onClick={handleIsActive}
            sx={{ padding: "15px" }}
            className="status-wrapper"
          >
            <Box
              className={`status-toggle1 ${isActive ? "active" : "Disable"}`}
            >
              <Box className="status-circle" />
            </Box>

            <Typography
              sx={{ width: "30px" }}
              className="active-txt"
              component="span"
            >
              {isActive ? "Active" : "Disable"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogActions>
        <Button
          onClick={handleCancel}
          className="cancel-button"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          disabled={pdferror != null}
          onClick={handleSave}
          className="submit-button"
        >
          {editingIndex !== null ? "Update" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IndiaPDFFormDialog;
